'use memo';

import { useCallback, useContext, useState } from 'react';
import {
  AgxPropertyAddressForm,
  AustralianState,
  PropertyAddress,
  SuburbSuggestions,
} from '@urbanx/agx-ui-components';
import {
  DevCampaignScenarioContractStage,
  DevCampaignScenarioCreateValues,
  DevCampaignScenarioCreatedCampaign,
  DevCampaignScenarioDialog,
  DevCampaignScenarioPropertyAddress,
  DevCampaignScenarioState,
} from '@urbanx/agx.ui-components2';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useParams } from 'react-router-dom';
import { progressDevContractWorkflow } from 'services/functions/activities/progressDevContractWorkflow';
import { createDevContractTestCampaign } from 'services/functions/campaigns/createDevContractTestCampaign';
import { getSuburbs } from 'services/functions/rex/getSuburbs';

interface DevCampaignScenarioModalProps {
  onClose: () => void;
}

interface ManualAddressInputProps {
  state: DevCampaignScenarioState;
  disabled: boolean;
  value: DevCampaignScenarioPropertyAddress;
  onChange: (value: DevCampaignScenarioPropertyAddress) => void;
  onValidityChange: (isValid: boolean) => void;
}

const toAustralianState = (state: DevCampaignScenarioState) =>
  state as AustralianState;

const getStaffPortalCampaignUrl = ({
  campaignId,
  agencyId,
}: DevCampaignScenarioCreatedCampaign) =>
  `${window.location.origin}/agencies/${agencyId}/campaigns/${campaignId}/campaign-tasks`;

const ManualAddressInput = ({
  state,
  disabled,
  value,
  onChange,
  onValidityChange,
}: ManualAddressInputProps) => {
  const { getAuthToken } = useContext(AuthContext);
  const [suburbSuggestions, setSuburbSuggestions] = useState<
    SuburbSuggestions[]
  >([]);

  const getSuburbSuggestions = useCallback(
    async (query: string) => {
      if (query.length < 3) {
        setSuburbSuggestions([]);
        return;
      }

      const authToken = await getAuthToken?.();
      if (!authToken) return;

      const controller = new AbortController();
      const result = await getSuburbs(
        authToken,
        query,
        toAustralianState(state),
        controller.signal
      );

      setSuburbSuggestions(
        result.map(
          (suggestion: {
            stateOrRegion: AustralianState;
            suburbOrTown: string;
            postCode: string;
          }) => ({
            state: suggestion.stateOrRegion,
            postcode: suggestion.postCode,
            suggestionLine1: suggestion.suburbOrTown,
            suggestionLine2: `${suggestion.stateOrRegion} ${suggestion.postCode}`,
          })
        )
      );
    },
    [getAuthToken, state]
  );

  return (
    <AgxPropertyAddressForm
      key={`dev-campaign-scenario-manual-address-${state}`}
      id="dev-campaign-scenario-manual-address"
      searchSuburb
      hideUnitLot
      showSearchForAddress={false}
      propertyAddressField={false}
      suburbSuggestions={suburbSuggestions}
      stateToSearch={toAustralianState(state)}
      onFetchSuburbSuggestions={suburbTerm =>
        getSuburbSuggestions(suburbTerm || '')
      }
      onClearSuburbSuggestions={() => setSuburbSuggestions([])}
      onDisableNextButton={disabled => onValidityChange(!disabled)}
      disabled={disabled}
      required
      defaultValue={value as PropertyAddress}
      onAddressSelected={selectedAddress => {
        if (selectedAddress) onChange(selectedAddress);
      }}
    />
  );
};

export const DevCampaignScenarioModal = ({
  onClose,
}: DevCampaignScenarioModalProps) => {
  const { getAuthToken } = useContext(AuthContext);
  const { agencyId } = useParams();

  const getRequiredAuthToken = useCallback(async () => {
    const authToken = await getAuthToken?.();
    if (!authToken) throw new Error('Unable to authenticate dev seeder call.');
    return authToken;
  }, [getAuthToken]);

  const handleCreate = useCallback(
    async (values: DevCampaignScenarioCreateValues) => {
      if (!agencyId) {
        throw new Error(
          'Create Test Campaign requires an agency-scoped Staff Portal route.'
        );
      }

      const authToken = await getRequiredAuthToken();

      return createDevContractTestCampaign(authToken, {
        createFrom: 'ManualAddress',
        agencyId,
        state: values.state,
        propertyAddress: {
          ...values.manualAddress,
          state: values.manualAddress.state || values.state,
        },
        agreementSource: values.agreementSource,
        agreementDocuSignSigned: values.agreementDocuSignSigned,
        listingLaunch: values.listingLaunch,
        contract: values.contract,
        contractCreationSource: 'AgentPortal',
        contractConditions: values.conditions,
        contractStage: values.contractStage,
        depositProfile: values.depositProfile,
      });
    },
    [agencyId, getRequiredAuthToken]
  );

  const handleProgressContractWorkflow = useCallback(
    async (
      campaign: DevCampaignScenarioCreatedCampaign,
      status: DevCampaignScenarioContractStage
    ) => {
      if (!campaign.contractId) return;

      const authToken = await getRequiredAuthToken();
      await progressDevContractWorkflow(authToken, {
        campaignId: campaign.campaignId,
        contractId: campaign.contractId,
        status,
      });
    },
    [getRequiredAuthToken]
  );

  return (
    <DevCampaignScenarioDialog
      allowRpDataSearch={false}
      onClose={onClose}
      onCreate={handleCreate}
      onProgressContractWorkflow={handleProgressContractWorkflow}
      getStaffPortalUrl={getStaffPortalCampaignUrl}
      renderManualAddressForm={props => <ManualAddressInput {...props} />}
    />
  );
};

export default DevCampaignScenarioModal;
